import { Grid, Typography } from '@mui/material';
import {
    DeviceThermostat,
    MonitorWeight,
    Favorite,
    Opacity,
    FavoriteBorder,
    Air
} from '@mui/icons-material';
import { t } from 'i18next';
import { useLatestMeasurements } from '../../../../hooks/useMeasurements';
import StatCard from './StatCard';
import HrvCard from './HrvCard';

const prepareData = (array, key) =>
    (array || [])
        .sort((a, b) => new Date(a.ts) - new Date(b.ts))
        .map((d) => ({ ts: d.ts, value: d[key] }));

const prepareBloodPressureData = (array) =>
    (array || [])
        .sort((a, b) => new Date(a.ts) - new Date(b.ts))
        .map((d) => ({
            ts: d.ts,
            systolic: d.systolic,
            diastolic: d.diastolic
        }));

export default function HealthStats({ patient }) {
    const { data, isLoading, error } = useLatestMeasurements(patient);

    if (isLoading) return <Typography>Loading...</Typography>;
    if (error) return <Typography>Error loading data</Typography>;

    const stats = [
        {
            key: 'weight',
            label: t('userPage.metrics.weight'),
            unit: 'kg',
            icon: <MonitorWeight sx={{ fontSize: 40, color: '#1976d2' }} />,
            current: data?.M_WEIGHT?.[0]?.weight ?? '—',
            currentDate: data?.M_WEIGHT?.[0]?.ts ?? null,
            previous: data?.M_WEIGHT?.[1]?.weight ?? '—',
            previousDate: data?.M_WEIGHT?.[1]?.ts ?? null,
            chart: prepareData(data?.M_WEIGHT, 'weight'),
            chartType: 'line'
        },
        {
            key: 'blood_pressure',
            label: t('userPage.metrics.bloodPressure'),
            unit: 'mmHg',
            icon: <Favorite sx={{ fontSize: 40, color: '#1976d2' }} />,
            current: data?.M_BLOOD_PRESSURE?.[0]
                ? `${data.M_BLOOD_PRESSURE[0].systolic}/${data.M_BLOOD_PRESSURE[0].diastolic}`
                : '—',
            currentDate: data?.M_BLOOD_PRESSURE?.[0]?.ts ?? null,
            previous: data?.M_BLOOD_PRESSURE?.[1]
                ? `${data.M_BLOOD_PRESSURE[1].systolic}/${data.M_BLOOD_PRESSURE[1].diastolic}`
                : '—',
            previousDate: data?.M_BLOOD_PRESSURE?.[1]?.ts ?? null,
            chart: prepareBloodPressureData(data?.M_BLOOD_PRESSURE),
            chartType: 'dual-line'
        },
        {
            key: 'blood_oxygen',
            label: t('userPage.metrics.bloodOxygen'),
            unit: '%',
            icon: <Air sx={{ fontSize: 40, color: '#1976d2' }} />,
            current: data?.M_BLOOD_OXYGEN?.[0]?.oxygen ?? '—',
            currentDate: data?.M_BLOOD_OXYGEN?.[0]?.ts ?? null,
            previous: data?.M_BLOOD_OXYGEN?.[1]?.oxygen ?? '—',
            previousDate: data?.M_BLOOD_OXYGEN?.[1]?.ts ?? null,
            chart: prepareData(data?.M_BLOOD_OXYGEN, 'oxygen'),
            chartType: 'line'
        },
        {
            key: 'blood_sugar',
            label: t('userPage.metrics.bloodSugar'),
            unit: 'mg/dL',
            icon: <Opacity sx={{ fontSize: 40, color: '#1976d2' }} />,
            current: data?.M_BLOOD_SUGAR?.[0]?.glicose ?? '—',
            currentDate: data?.M_BLOOD_SUGAR?.[0]?.ts ?? null,
            previous: data?.M_BLOOD_SUGAR?.[1]?.glicose ?? '—',
            previousDate: data?.M_BLOOD_SUGAR?.[1]?.ts ?? null,
            chart: prepareData(data?.M_BLOOD_SUGAR, 'glicose'),
            chartType: 'bar'
        },
        {
            key: 'body_temperature',
            label: t('userPage.metrics.temperature'),
            unit: '°C',
            icon: <DeviceThermostat sx={{ fontSize: 40, color: '#1976d2' }} />,
            current: data?.M_BODY_TEMPERATURE?.[0]?.temperature ?? '—',
            currentDate: data?.M_BODY_TEMPERATURE?.[0]?.ts ?? null,
            previous: data?.M_BODY_TEMPERATURE?.[1]?.temperature ?? '—',
            previousDate: data?.M_BODY_TEMPERATURE?.[1]?.ts ?? null,
            chart: prepareData(data?.M_BODY_TEMPERATURE, 'temperature'),
            chartType: 'line'
        },
        {
            key: 'heart_rate',
            label: t('userPage.metrics.heartRate'),
            unit: 'bpm',
            icon: <FavoriteBorder sx={{ fontSize: 40, color: '#1976d2' }} />,
            current: data?.M_HEARTRATE?.[0]?.hr ?? '—',
            currentDate: data?.M_HEARTRATE?.[0]?.ts ?? null,
            previous: data?.M_HEARTRATE?.[1]?.hr ?? '—',
            previousDate: data?.M_HEARTRATE?.[1]?.ts ?? null,
            chart: prepareData(data?.M_HEARTRATE, 'hr'),
            chartType: 'line'
        }
    ];

    const STAT_CARD_HEIGHT = 220;
    const GRID_VERTICAL_GAP_PX = 16;
    const HRV_CARD_HEIGHT_LG = STAT_CARD_HEIGHT * 2 + GRID_VERTICAL_GAP_PX;

    return (
        <Grid container spacing={2} sx={{ mb: 3 }}>
            {/*<Grid item xs={12} lg={7}>
                <Grid container spacing={2}>*/}
            {stats.map((stat, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                    <StatCard {...stat} />
                </Grid>
            ))}
            {/*</Grid>
            </Grid>

            <Grid item xs={12} lg={5} sx={{ display: 'flex' }}>
                <HrvCard fixedHeight={{ xs: null, lg: `${HRV_CARD_HEIGHT_LG}px` }} />
            </Grid>*/}
        </Grid>
    );
}
